'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import PodcastCard from './PodcastCard';

const ITEMS_PER_PAGE = 12;

function SearchIcon() {
    return (
        <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
        </svg>
    );
}

function ClearIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
    );
}

const SORT_OPTIONS = [
    { value: 'publishDate_desc', label: 'Newest first' },
    { value: 'publishDate_asc', label: 'Oldest first' },
    { value: 'title_asc', label: 'Title (A–Z)' }
];

export default function PodcastListClient({ initialPodcasts = [], topics = [] }) {
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const [activeTopics, setActiveTopics] = useState(new Set());
    const [sort, setSort] = useState('publishDate_desc');
    const [podcasts, setPodcasts] = useState(initialPodcasts.slice(0, ITEMS_PER_PAGE));
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(initialPodcasts.length >= ITEMS_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const initialRef = useRef(initialPodcasts);
    const abortRef = useRef(null);

    const isFiltered = !!search || activeTopics.size > 0 || sort !== 'publishDate_desc';

    useEffect(() => {
        const timer = setTimeout(() => setSearch(inputValue.trim()), 350);
        return () => clearTimeout(timer);
    }, [inputValue]);

    const doFetch = useCallback(async ({ s, t, sortValue, p, append = false }) => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: String(ITEMS_PER_PAGE),
                page: String(p),
                sort: sortValue
            });
            if (s) params.set('search', s);
            t.forEach((topic) => params.append('topic', topic));

            const res = await fetch(`/api/podcast?${params}`, { signal: controller.signal });
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            const data = await res.json();
            const fetched = data.podcasts || [];

            setPodcasts((prev) => (append ? [...prev, ...fetched] : fetched));
            setHasMore(data.hasMore ?? false);
            setIsLoading(false);
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Failed to fetch podcasts', err);
                setError('Something went wrong. Please try again.');
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        setPage(1);
        const hasFilters = search || activeTopics.size > 0 || sort !== 'publishDate_desc';
        if (hasFilters) {
            doFetch({ s: search, t: [...activeTopics], sortValue: sort, p: 1 });
        } else {
            setPodcasts(initialRef.current.slice(0, ITEMS_PER_PAGE));
            setHasMore(initialRef.current.length >= ITEMS_PER_PAGE);
            setError(null);
        }
    }, [search, activeTopics, sort, doFetch]);

    const toggleTopic = (topic) => {
        setActiveTopics((prev) => {
            const next = new Set(prev);
            if (next.has(topic)) {
                next.delete(topic);
            } else {
                next.add(topic);
            }
            return next;
        });
    };

    const handleLoadMore = () => {
        const next = page + 1;
        setPage(next);
        doFetch({
            s: search,
            t: [...activeTopics],
            sortValue: sort,
            p: next,
            append: true
        });
    };

    const clearFilters = () => {
        setInputValue('');
        setSearch('');
        setActiveTopics(new Set());
        setSort('publishDate_desc');
    };

    return (
        <section className="w-full px-5 py-12">
            <div className="container p-0">
                <div className="mb-10 flex flex-col gap-5">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="relative w-full max-w-lg">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Search episodes..."
                                aria-label="Search podcast episodes"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                className="w-full rounded-xl border border-white/20 bg-white/10 py-3 pl-12 pr-4 text-white outline-none transition placeholder:text-white/40 focus:border-white/40 focus:bg-white/15"
                            />
                            {inputValue && (
                                <button
                                    onClick={() => {
                                        setInputValue('');
                                        setSearch('');
                                    }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 transition hover:text-white"
                                    aria-label="Clear search">
                                    <ClearIcon />
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <label
                                htmlFor="podcast-sort"
                                className="shrink-0 text-sm text-white/60">
                                Sort by
                            </label>
                            <select
                                id="podcast-sort"
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="cursor-pointer rounded-xl border border-white/20 bg-white/10 py-2.5 pl-4 pr-10 text-sm text-white outline-none transition focus:border-white/40 focus:bg-white/15">
                                {SORT_OPTIONS.map((opt) => (
                                    <option
                                        key={opt.value}
                                        value={opt.value}
                                        className="bg-gray-900 text-white">
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {topics.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={clearFilters}
                                className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                                    !isFiltered
                                        ? 'bg-white text-gray-900'
                                        : 'border border-white/25 text-white/60 hover:border-white/50 hover:text-white'
                                }`}>
                                All episodes
                            </button>
                            {topics.map((topic) => (
                                <button
                                    key={topic}
                                    onClick={() => toggleTopic(topic)}
                                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                                        activeTopics.has(topic)
                                            ? 'bg-white text-gray-900'
                                            : 'border border-white/25 text-white/60 hover:border-white/50 hover:text-white'
                                    }`}>
                                    {topic}
                                </button>
                            ))}
                        </div>
                    )}

                    {isFiltered && (
                        <div className="flex items-center gap-3 text-sm text-white/50">
                            <span>
                                {podcasts.length} episode{podcasts.length === 1 ? '' : 's'} found
                            </span>
                            <button
                                onClick={clearFilters}
                                className="text-white/70 underline hover:text-white">
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {error ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-24 text-white/60">
                        <p className="text-lg">{error}</p>
                    </div>
                ) : isLoading && !podcasts.length ? (
                    <div className="flex items-center justify-center py-24">
                        <div className="size-10 animate-spin rounded-full border-4 border-white/20 border-t-white" />
                    </div>
                ) : podcasts.length ? (
                    <>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {podcasts.map((podcast, i) => (
                                <PodcastCard
                                    key={podcast.identifier || i}
                                    podcast={podcast}
                                    priority={i < 4}
                                />
                            ))}
                        </div>
                        {hasMore && (
                            <div className="mt-12 flex justify-center">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                    className="rounded-xl border border-white/25 px-10 py-3 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-40">
                                    {isLoading ? (
                                        <span className="flex items-center gap-2">
                                            <span className="inline-block size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                            Loading…
                                        </span>
                                    ) : (
                                        'Load more'
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-3 py-24 text-white/50">
                        <p className="text-lg">No episodes found</p>
                        {isFiltered && (
                            <button
                                onClick={clearFilters}
                                className="text-sm underline hover:text-white">
                                Clear filters
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
